import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import {
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import Breakpoint from 'components/Common/Breakpoint'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import SinglePropertyHotelFlightsToggleButton from 'components/OfferPage/Accommodation/HotelFlightsToggle/SinglePropertyHotelFlightsToggleButton'
import useMobilePackageSelectCtaExperimentState from 'components/OfferPage/Accommodation/useMobilePackageSelectCtaExperimentState'
import AlwaysOnMobileStickyHeaderFilters from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnMobileStickyHeaderFilters'
import OfferAirportSelectBox from 'components/OfferPage/Filters/Airport/OfferAirportSelectBox'
import OfferCapacityWithAgesSelect from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDatePickerSelect from 'components/OfferPage/Filters/DatePicker/OfferDatePickerSelect'
import OfferDatePickerSelectBox from 'components/OfferPage/Filters/DatePicker/OfferDatePickerSelectBox'
import type { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import OfferDurationTabs from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import OfferDesktopFlightToggle from 'components/OfferPage/Filters/FlightToggle/OfferDesktopFlightToggle'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'
import OfferPageFlightDealBanner from 'components/OfferPage/OfferPageFlightDealBanner'
import OfferPageGallery from 'components/OfferPage/OfferPageGallery'
import SocialProofDialog from 'components/OfferPage/SocialProofDialog'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { verticalChildSpacing } from 'components/utils/spacing'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import {
  queryKeyPriceTotal,
  queryKeyRoomRateGroupId,
  queryKeyRoomRateId,
  queryKeyRoomTypeId,
  urlHashDatePicker,
} from 'constants/url'
import ModalProvider from 'contexts/ModalProvider'
import type {
  OfferPageFilterType,
  OfferPageRoom,
} from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  initialOfferPageRooms,
} from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import GeoContext from 'contexts/geoContext'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAvailabilityForDate } from 'hooks/OfferPage/useAvailabilityForDate'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { SOCIAL_PROOF_COPY_VARIANTS } from 'hooks/OfferPage/useSocialProofPopup'
import useOfferTags from 'hooks/Offers/useOfferTags'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useFlightPrice from 'hooks/useFlightPrice'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import usePropertyRates from 'hooks/usePropertyRates'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useShareModalConfig from 'hooks/useShareModalConfig'
import isAuDomesticAirportRoute from 'lib/flights/isAuDomesticAirportRoute'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { countOccupantsForFlights, isValidFlightsOccupants } from 'lib/offer/occupancyUtils'
import { hasSelectedRooms, hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { replace } from 'lib/routing/appRouting'
import { encodeSearchParams, isRoomsInvalid } from 'lib/search/searchUtils'
import { hasDifferentPackagesForRooms } from 'selectors/offerPage/multipleRoomBookingSelectors'
import {
  getCheckoutDate,
  getPropertyDurationOptions,
  getPropertyLoggedOutSavedPercent,
  getPropertyRatesKey,
  getShouldShowPropertyAnytimeRate,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'

import PropertyIntro from './PropertyIntro'
import PropertyQuickFilters from './PropertyQuickFilters'
import PropertyRoomOptions from './PropertyRoomOptions'
import PropertySidebarFilters from './PropertySidebarFilters'
import PropertyStickyFooter from './PropertyStickyFooter'
import PropertyStickyHeader from './PropertyStickyHeader'
import PropertyUnavailableSingleUnitRecommendations from './PropertyUnavailableSingleUnitRecommendations'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;

  ${verticalChildSpacing[8]};
`

const ResponsivePackageOptionsSpacer = styled.div`
  & > * + * {
    margin-top: ${rem(8)};
  }
  & > [data-large-gap] + * {
    margin-top: ${rem(32)};
  }
`

const QuickFiltersRoomsGroup = styled(Group)`
  && {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    && {
      margin-top: ${rem(32)};
    }
  }
`

// Mirrors LayoutContainer on tablet+ but is a passthrough on mobile so the slider can sit full-width.
const ResponsiveSliderInnerWrapper = styled.div`
  width: 100%;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
    margin-inline: auto;
    max-width: ${rem(1172)};
  }
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferDatePickerSelect = styled(OfferDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;

  &:has(> :nth-child(3)) > * {
    flex: 1;
    max-width: unset;
  }
`

const FullBleedOnMobileContainer = styled(LayoutContainer)`
  ${mediaQueryDown.mobile} {
    padding: 0;
  }
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  socialProofRecentPurchases: Array<App.OfferRecentPurchase> | undefined
  socialProofImage: App.Image
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  flights?: boolean
  areAnytimeRates: boolean
  shouldShowPropertyAnytimeRates: boolean
  durationOptions: Array<OfferDurationOption>
  availableForPurchase?: boolean
  loggedOutSavedPercent: number | undefined
  room?: App.PackageRoomType
  canBookNowMultiRoom: boolean
  utmContent: string | undefined
  isAlternativeDatesFlow: boolean
}

const currentTimer = {} as Record<string, any>

const slideTimer = (name: string, callback: any, ms: number) => {
  if (currentTimer[name]) {
    clearTimeout(currentTimer[name])
  }
  currentTimer[name] = setTimeout(callback, ms)
}

function PropertyContent(props: Props) {
  const {
    property,
    offer,
    socialProofRecentPurchases,
    socialProofImage,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    flights,
    areAnytimeRates,
    shouldShowPropertyAnytimeRates,
    durationOptions,
    availableForPurchase,
    loggedOutSavedPercent,
    room,
    canBookNowMultiRoom,
    activeHeaderFilter,
    utmContent,
    isAlternativeDatesFlow,
  } = props
  const destinationAirportCode = property.airportCode

  const intl = useIntl()
  const location = useLocation()
  const query = useQsQueryParams<{
    roomRateId?: string
    roomRateGroupId?: string
    roomTypeId?: string
    roomOptionRateId?: string
  }>()
  const { originAirports, airports } = useContext(GeoContext)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)
  const { tags: offerContentTags } = useOfferTags(offer.id)

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const isMobileDevice = useScreenSizeOnly('mobile')
  // NB: This experiment is currently disabled, and should _not_ be re-enabled until we've rewritten
  // all the mobile flow components.
  const { isAnyVariant: isNewMobileBookingFlowVariant } = useExperiment(
    Experiments.croAlwaysOnNewMobileBooking,
    { canActivate: isMobileDevice },
  )
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowVariant
  const invalidRoomSelectionForSticky = !hasSelectedRoomsOrDefault(rooms) || isRoomsInvalid(rooms)
  const canViewOptionsForSticky = !!checkInDate && !invalidRoomSelectionForSticky
  const {
    ctaMode: mobileAccommodationStickyFooterCtaMode,
    shouldMountStickyFooter: shouldMountMobileAccommodationStickyFooter,
  } = useMobilePackageSelectCtaExperimentState({
    availableForPurchase,
    canShowCheckAvailabilityCta: useNewMobileBookingFlow && !canViewOptionsForSticky,
    canShowPackageSelectCta: useNewMobileBookingFlow && canViewOptionsForSticky,
    canBookNowMultiRoom,
    room,
  })

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      flights,
      paramsToKeep: [queryKeyRoomRateId, queryKeyRoomRateGroupId, queryKeyRoomTypeId, queryKeyPriceTotal],
    }).toString(), [checkInDate, checkOutDate, rooms, location.search, flights])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useAvailabilityForDate(
    offer.id,
    rooms,
    isAlternativeDatesFlow ? checkInDate : undefined,
    isAlternativeDatesFlow ? duration : undefined,
  )

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentOccupancyNumber = rooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? initialOfferPageRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, activeHeaderFilter, previousFilter, hasRoomsChanged]) // oxlint-disable-line react-hooks/exhaustive-deps

  const roomOption = useMemo(() => {
    if (!query.roomTypeId) return
    // Typically, `roomRateId` is for `gha-lpc` and `roomOptionRateId` is for `gha-lpp`.
    const roomRateId = query.roomRateId || query.roomOptionRateId
    if (!roomRateId) return
    return { roomTypeId: query.roomTypeId, roomRateId, roomRateGroupId: query.roomRateGroupId }
  }, [query.roomTypeId, query.roomRateId, query.roomRateGroupId, query.roomOptionRateId])
  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
  }, [stateDispatch, roomOption])

  useEffect(() => {
    slideTimer('fetchRelatedTravelItems', () => {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }, 2000)
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // Ensure property rates are loaded.
  usePropertyRates({
    offerType: OFFER_TYPE_ALWAYS_ON,
    offerId: offer.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    occupancies: rooms,
    utmContent,
    roomTypeId: roomOption?.roomTypeId,
    roomRateId: roomOption?.roomRateId,
    roomRateGroupId: roomOption?.roomRateGroupId,
  })
  // Ensure cheapest flights are also loaded. Note that (for now) we'll assume: (i) both LPC and
  // bedbank offers are configured identically (i.e. both have flights enabled and the same
  // destination airport code); and (ii) there's no `forceBundleId` for LPC offers (or its discount
  // is immaterial).
  useFlightPrice(
    destinationAirportCode,
    rooms,
    checkInDate,
    checkOutDate,
    airport?.code,
    duration,
    { disabled: !property.areFlightsEnabled },
  )

  const occupancyLimits = useMemo(() => {
    return Object.values(property.rooms).flatMap(({ capacities }) => capacities)
  }, [property.rooms])

  const isValidFlightsOccupancy = useMemo(() => {
    const flightOccupants = countOccupantsForFlights(rooms)
    return isValidFlightsOccupants(flightOccupants)
  }, [rooms])
  // Only show flights if: (i) they're enabled at the (brand/)property-level; and (ii) either we
  // _have_ to sell packaged rates or the selected room occupancies are valid for flights.
  const shouldShowFlights = property.areFlightsEnabled && (property.mustBundleWithFlights || isValidFlightsOccupancy)
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === destinationAirportCode), [airports, destinationAirportCode])
  const isAuDomesticFlight = useMemo(() => isAuDomesticAirportRoute({
    airports,
    departureAirportCode: airport?.code,
    destinationAirportCode,
  }), [airport?.code, airports, destinationAirportCode])
  const showFlightInformation = destinationAirport &&
    (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) &&
    shouldShowFlights

  const shouldShowDurationTabs = useMemo(() => durationOptions.some((option) => {
    return !!(option.hasBonusInclusions || option.hasTactical || option.hasPromotion)
  }), [durationOptions])
  const onBeforeDurationClick = useCallback((duration: number) => {
    if (!(areAnytimeRates && shouldShowPropertyAnytimeRates)) return true
    if (duration !== property.marketedLos) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      return false
    }
    return true
  }, [areAnytimeRates, property.marketedLos, shouldShowPropertyAnytimeRates, stateDispatch])

  // Disable quick filters if we're even remotely dealing with multiple rooms.
  const shouldShowQuickFilters = rooms.length <= 1

  const showDealBanner = airport && destinationAirport
  const showFlightDealBanner = !!showDealBanner && config.FLIGHT_ENABLED && !isAgentHubEnabled && !checkInDate
  const showOfferInfoBanner = !!offerContentTags[0]?.name || showFlightDealBanner

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)
  const offerType = isAgentHubEnabled && isBedbankOffer(offer) ? 'bedbank_hotel' : 'hotel'

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType,
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  return <ProductPaletteProvider product={offer}>
    <SocialProofDialog
      copyVariant={SOCIAL_PROOF_COPY_VARIANTS.sppStay}
      experiment={Experiments.croSocialProofSpp}
      offerImage={socialProofImage}
      recentPurchases={socialProofRecentPurchases}
    />
    <TripGuard>
      <ImmersiveSaveButtonPortal>
        <BookmarkButton offer={offer} />
      </ImmersiveSaveButtonPortal>
    </TripGuard>
    <RelativeBlock onSubmit={(e) => e.preventDefault()}>
      {useNewMobileBookingFlow && <AccommodationOfferPageStickyHeaderMobile>
        <AlwaysOnMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>}
      <div>
        <CSSBreakpoint min="tablet" dynamic>
          <PropertyStickyHeader
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
        </CSSBreakpoint>
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        {availableForPurchase && (
          <WalledContent enforced={property.walledGarden}>
            <Breakpoint max="mobile">
              <ModalProvider>
                <PropertySidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                  durationOptionsOverride={durationOptions}
                />
              </ModalProvider>
            </Breakpoint>
          </WalledContent>
        )}
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <Pane>
          <PropertyIntro
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
      </div>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        <PackageOptionsPane id="Package-Options">
          <ResponsivePackageOptionsSpacer>
            <LayoutContainer data-large-gap="">
              <VerticalSpacer gap={32}>
                {availableForPurchase && (
                  <WalledContent enforced={property.walledGarden}>
                    <>
                      {!useNewMobileBookingFlow && (
                        <CSSBreakpoint max="mobile" dynamic>
                          <Group direction="vertical" gap={16}>
                            {/* We've temporarily disabled `cro_lpc_new_mobile_booking`, so we're on
                            the old mobile flow. However, we're not showing hotel-only and
                            hotel-with-flights pricing side-by-side, so we need this toggle to be
                            present. */}
                            <SinglePropertyHotelFlightsToggleButton />
                            <Group direction="vertical" gap={8}>
                              <OfferCapacityWithAgesSelectBox />
                              <OfferDatePickerSelectBox />
                              {showFlightInformation && flights && <OfferAirportSelectBox />}
                            </Group>
                          </Group>
                        </CSSBreakpoint>
                      )}
                      <CSSBreakpoint min="tablet" dynamic>
                        <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                        <FlexDesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" wrap="wrap" className={clsx({ open: !!activeFilter })}>
                          <StyledOfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                          <StyledOfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                          {property.areFlightsEnabled && <OfferDesktopFlightToggle
                            bundledWithFlightsOnly={property.mustBundleWithFlights}
                            isAuDomesticFlight={isAuDomesticFlight}
                          />}
                          <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                        </FlexDesktopFilterBlock>
                      </CSSBreakpoint>
                    </>
                  </WalledContent>
                )}
              </VerticalSpacer>
            </LayoutContainer>
            <OfferPageLoyaltyPointsSlider InnerWrapper={ResponsiveSliderInnerWrapper} flushOnMobile />
            {shouldShowDurationTabs && !useNewMobileBookingFlow && <LayoutContainer>
              <VerticalSpacer gap={24}>
                <VerticalSpacer gap={8}>
                  <BodyText variant="large" align="center">
                    {intl.formatMessage({
                      id: 'J45fSGD5me9d0j5jTAfG',
                      defaultMessage: 'Select duration',
                      description: 'Text label for duration selection section on property page',
                    })}
                  </BodyText>
                  <OfferDurationTabs
                    durationOptionsOverride={durationOptions}
                    onBeforeDurationClick={onBeforeDurationClick}
                    availableForPurchaseOverride={availableForPurchase}
                  />
                </VerticalSpacer>
              </VerticalSpacer>
            </LayoutContainer>}
            {showOfferInfoBanner && <LayoutContainer>
              <VerticalSpacer gap={16}>
                <ContentTagBanner offerId={offer.id} page="offer" />
                {showFlightDealBanner && <OfferPageFlightDealBanner
                  destinationAirportCode={destinationAirport.code}
                  originAirportCode={airport.code}
                />}
              </VerticalSpacer>
            </LayoutContainer>}
            {shouldShowQuickFilters && <QuickFiltersRoomsGroup direction="vertical" gap={0} tabletGap={32} horizontalAlign="stretch">
              <FullBleedOnMobileContainer>
                <PropertyQuickFilters />
              </FullBleedOnMobileContainer>
              <FullBleedOnMobileContainer>
                {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
                  onViewPackageOptions={scrollToPackageOptions}
                  flushTop
                />}
              </FullBleedOnMobileContainer>
            </QuickFiltersRoomsGroup>}
            {!shouldShowQuickFilters && <FullBleedOnMobileContainer>
              {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
                onViewPackageOptions={scrollToPackageOptions}
              />}
            </FullBleedOnMobileContainer>}
            {showFlightInformation && <LayoutContainer>
              <Caption variant="medium" align="center">
                {intl.formatMessage(
                  {
                    id: 'V6oeW3V4xUi9qn30TFXQ',
                    defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                    description: 'Flight pricing disclaimer text in the flight search or booking flow',
                  },
                  { brandName: config.brandConfig.name },
                )}
              </Caption>
            </LayoutContainer>}
          </ResponsivePackageOptionsSpacer>
        </PackageOptionsPane>
        {!useNewMobileBookingFlow && availableForPurchase &&
          <PropertyStickyFooter
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
        {useNewMobileBookingFlow && availableForPurchase && shouldMountMobileAccommodationStickyFooter && (
          <AccommodationOfferPageStickyFooter ctaMode={mobileAccommodationStickyFooterCtaMode} />
        )}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    <ErrorBoundary fallback={null}>
      <PropertyUnavailableSingleUnitRecommendations />
    </ErrorBoundary>
  </ProductPaletteProvider>
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flights'
  | 'room'
  | 'areAnytimeRates'
  | 'shouldShowPropertyAnytimeRates'
  | 'durationOptions'
  | 'availableForPurchase'
  | 'loggedOutSavedPercent'
  | 'canBookNowMultiRoom'
  | 'activeHeaderFilter'
  | 'utmContent'
  | 'isAlternativeDatesFlow'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flights: state.flights,
  room: state.room,
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  shouldShowPropertyAnytimeRates: getShouldShowPropertyAnytimeRate(state, appState),
  durationOptions: getPropertyDurationOptions(state),
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  loggedOutSavedPercent: getPropertyLoggedOutSavedPercent(state, appState),
  canBookNowMultiRoom: !!state.offer && isMultipleRoomBookingActive(state, appState) && hasDifferentPackagesForRooms(state, appState),
  activeHeaderFilter: state.activeHeaderFilter,
  utmContent: appState.router.location.query.utm_content,
  isAlternativeDatesFlow: !!state.isAlternativeDatesFlow,
}), PropertyContent)
